import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { DimensionType } from "../../../api/dimensionType";
import {
  useCreateMeasureMutation,
  useGetProjectStateQuery,
} from "../../../api/managementApi";
import { namespaces } from "../../../consts/i18n";
import useSearchMeasures from "../../../hooks/useSearchMeasures";
import useSearchPanels from "../../../hooks/useSearchPanels";
import useSearchRecords from "../../../hooks/useSearchRecords";
import useViewerDisplayMode from "../../../hooks/useViewerDisplayMode";
import { getUniqueName } from "../../../redux/measures/measuresReducer";
import {
  addNonStoredMeasure,
  removeNonStoredMeasure,
} from "../../../redux/measures2/measures2Reducer";
import { addMessage } from "../../../redux/messages/messagesReducer";
import { toolModeType } from "../../../redux/pointCloudViewer/pointCloudViewerReducer";
import { DisplayType } from "../../../redux/viewer/viewerReducer";
import CircleButton from "../../buttons/CircleButton";
import CreateMeasureDialog from "../../dialogs/CreateMeasureDialog";
import SearchBar from "../../inputs/SearchBar";
import DisplayTypeTabs from "./DisplayTypeTabs";
import MeasureFilter from "./MeasureFilter";
import MeasureList from "./MeasureList";
import { PanelFilter } from "./PanelsList/PanelFilter";
import { PanelsList } from "./PanelsList/PanelsList";
import RecordFilter from "./RecordFilter";
import RecordList from "./RecordList";
import { DOWNLOAD_BASE_URL } from "../../../config";
import CreateRecordDialog from "../../dialogs/CreateRecordDialog";
import useAuthInfo from "../../../hooks/useAuthInfo";

/**
 * @param {{onCreateMeasure: Function, dimensionType: DimensionType}} props
 */
const Sidebar = ({ onCreateMeasure, dimensionType }) => {
  const dispatch = useDispatch();
  const nonStoredPanels = useSelector((state) => state.measures2.nonStored);
  const filteredPanelsList = useSelector(
    (state) => state.panel.filteredPanelsList,
  );

  const { projectId, stateId } = useParams();

  const { data: projectState } = useGetProjectStateQuery({
    projectId,
    id: stateId,
  });

  const [displayMode] = useViewerDisplayMode();

  const [isOpenCreateMeasureDialog, setIsOpenCreateMeasureDialog] =
    useState(false);

  const handleCreateMeasureClick = (name, type = toolModeType.DISTANCE) => {
    setIsOpenCreateMeasureDialog(false);
    onCreateMeasure(name, type);
  };

  const [isCreateRecordDialogOpen, setIsCreateRecordDialogOpen] =
    useState(false);
  const [createRecordInitialData, setCreateRecordInitialData] = useState(null);

  const { project } = useSelector((state) => state.info);

  const { canEdit } = useAuthInfo();

  const [createMeasure, deleteMeasure] = useCreateMeasureMutation();

  const { t } = useTranslation(namespaces.viewer);

  const handleStoreClick = useCallback(
    ({ measure, toBeStored }) => {
      if (dimensionType === DimensionType.D2 && toBeStored) {
        setCreateRecordInitialData({ measure });
        setIsCreateRecordDialogOpen(true);
        return;
      }

      if (toBeStored) {
        createMeasure({
          projectId: projectId,
          dimension: dimensionType,
          name: measure.name,
          type: measure.type,
          data: measure.points,
        })
          .unwrap()
          .then((res) => {
            dispatch(removeNonStoredMeasure(measure.id));
          });
      } else {
        deleteMeasure({ id: measure.dbId }).then(() => {
          dispatch(addNonStoredMeasure(measure));
        });
      }
    },
    [dimensionType, createMeasure, deleteMeasure, dispatch, projectId],
  );

  const [searchFold, setSearchFold] = useState(false);

  const handleToggleFoldSearch = () => {
    setSearchFold(!searchFold);
  };

  const handleExportProject = () => {
    window.open(
      `${DOWNLOAD_BASE_URL}/api/project/${projectId}/export-solar`,
      "_blank",
    );
  };

  const [measureSearchText, setMeasureSearchText] = useSearchMeasures();
  const [recordsSearchText, setRecordsSearchText] = useSearchRecords();
  const { panelsSearchText, setPanelsSearchText } = useSearchPanels();

  return (
    <div className="w-[525px] max-h-screen h-screen flex flex-col overflow-y-auto">
      <div className="h-14 max-h-14">
        <DisplayTypeTabs dimensionType={dimensionType} />
      </div>
      <div
        className="pt-8 pl-10 pr-5 flex flex-col grow"
        style={{ height: "calc(100vh - 54px)" }}
      >
        <div className="flex flex-row justify-between mb-[10px]">
          <div className="w-full">
            <div className="flex flex-row justify-between">
              <h3 className="text-3xl font-bold text-700 m-0 pr-4 w-1/2">
                {t("sidebar.title")} {project?.name}
              </h3>

              {displayMode === DisplayType.SOLAR_PANELS && (
                <div className="flex items-center justify-end gap-1 w-1/2">
                  {filteredPanelsList.length > 0 && canEdit && (
                    <CircleButton
                      icon="/images/export.svg"
                      primary
                      className="p-4 mr-3"
                      iconSize="12px"
                      onClick={handleExportProject}
                    />
                  )}

                  <button
                    onClick={() => {
                      dispatch(
                        addMessage({
                          type: "success",
                          text: t("sidebar.panels.addPanelOnClick"),
                        }),
                      );
                      onCreateMeasure(
                        getUniqueName(
                          t("sidebar.panels.unsavedPanel"),
                          nonStoredPanels,
                        ),
                        toolModeType.AREA,
                      );
                    }}
                    className="rounded-full min-w-fit text-sm gap-2 flex items-center justify-between leading-tight bg-primary text-white font-bold px-4 hover:bg-opacity-90 py-2"
                  >
                    <img
                      className={"pointer-events-none inline w-3 h-3"}
                      src="/images/viewer_measurecontrol_open.svg"
                      alt=""
                      width={12}
                      height={12}
                    />
                    <span className="ml-2">{t("sidebar.panels.newPanel")}</span>
                  </button>
                </div>
              )}

              {displayMode !== DisplayType.SOLAR_PANELS && (
                <CircleButton
                  icon="/images/sidebar_fold.svg"
                  primary
                  className={`w-[29px] h-[29px] transform  ${
                    searchFold ? "rotate-[180deg]" : "rotate-0"
                  } animate-transition transition-all
                `}
                  iconSize="12px"
                  onClick={handleToggleFoldSearch}
                />
              )}
            </div>
            <span className="text-sm font-bold m-0">
              {projectState?.name} - {t(`sidebar.view.${dimensionType}`)}
            </span>
          </div>
        </div>
        <div
          className={`transition-all ease-in-out ${
            searchFold
              ? "duration-200 overflow-hidden"
              : "duration-300 delay-150"
          } ${searchFold ? "max-h-0" : "max-h-[1000px]"}`}
        >
          <SearchBar
            onSearch={(searchText) => {
              if (displayMode === DisplayType.MEASURES) {
                setMeasureSearchText(searchText);
                return;
              }

              if (displayMode === DisplayType.RECORDS) {
                setRecordsSearchText(searchText);
                return;
              }

              if (displayMode === DisplayType.SOLAR_PANELS) {
                setPanelsSearchText(searchText);
                return;
              }
            }}
            value={
              displayMode === DisplayType.MEASURES
                ? measureSearchText
                : recordsSearchText
            }
          />

          {displayMode === DisplayType.MEASURES && (
            <MeasureFilter dimensionType={dimensionType} />
          )}
          {displayMode === DisplayType.RECORDS && <RecordFilter />}
          {displayMode === DisplayType.SOLAR_PANELS && <PanelFilter />}
        </div>
        <div className="grow pr-1 pb-8">
          {displayMode === DisplayType.MEASURES && (
            <MeasureList
              onStoreClick={handleStoreClick}
              dimensionType={dimensionType}
            />
          )}

          {displayMode === DisplayType.RECORDS && <RecordList />}
          {displayMode === DisplayType.SOLAR_PANELS && (
            <PanelsList
              searchText={panelsSearchText}
              dimension={dimensionType}
            />
          )}
        </div>
        <CreateMeasureDialog
          dimensionType={dimensionType}
          isOpen={isOpenCreateMeasureDialog}
          onClose={() => setIsOpenCreateMeasureDialog(false)}
          onCreate={handleCreateMeasureClick}
        />
        <CreateRecordDialog
          project={project}
          isOpen={isCreateRecordDialogOpen}
          initialData={createRecordInitialData}
          onClose={() => setIsCreateRecordDialogOpen(false)}
        />
      </div>
    </div>
  );
};

export default Sidebar;
